import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { Movie } from '@usheru-hff/shared/domain';

import {
  EnvironmentComponentConfig,
  EnvironmentComponents,
  MoreInfoParams,
  MovieCatalogueParams,
  MovieDetailInfoParams,
  MovieFeedParams,
  PrePurchasePageParams,
  ShowTimeStreamingSubscribeParams,
  UplayerParams,
  WatchAtHomeParams
} from './environment.interface';
import { ENVIRONMENT } from './environment.token';

@Injectable({
  providedIn: 'root'
})
export class EnvironmentService {
  private readonly env = inject(ENVIRONMENT);
  private readonly router = inject(Router);

  prepurchaseComponent<K extends keyof PrePurchasePageParams>(prop: K): PrePurchasePageParams[K] | undefined {
    return this.getComponentProp('prepurchasePage', prop);
  }

  uplayerComponent<K extends keyof UplayerParams>(prop: K): UplayerParams[K] | undefined {
    return this.getComponentProp('uplayer', prop);
  }

  movieCatalogueComponent<P extends MovieCatalogueKeys>(propPath: P): PathValue<MovieCatalogueParams, P> | undefined {
    return this.getComponentProp('movieCatalogue', propPath as string) as
      | PathValue<MovieCatalogueParams, P>
      | undefined;
  }

  showtimeStreamingSubscribe<K extends keyof ShowTimeStreamingSubscribeParams>(
    prop: K
  ): ShowTimeStreamingSubscribeParams[K] | undefined {
    return this.getComponentProp('showtimeStreamingSubscribe', prop);
  }

  movieFeedParams<K extends keyof MovieFeedParams>(prop: K): MovieFeedParams[K] {
    const DEFAULTS: {
      movieFeedParams: MovieFeedParams;
    } = {
      movieFeedParams: {
        paginationResults: 'concatenate'
      }
    };
    return this.getComponentProp('movieFeedParams', prop) ?? DEFAULTS.movieFeedParams[prop];
  }

  movieMoreInfoComponent<K extends keyof MovieDetailInfoParams>(prop: K): MovieDetailInfoParams[K] {
    const DEFAULTS: {
      movieMoreInfoParams: MovieDetailInfoParams;
    } = {
      movieMoreInfoParams: {
        showReleaseDate: (infoParams?: MoreInfoParams, movie?: Movie) =>
          infoParams?.releaseDate !== false &&
          !movie?.locReleaseDate &&
          !(movie?.releaseDate || infoParams?.releaseDateEmptyMessage),
        showLocReleaseDate: (infoParams?: MoreInfoParams, movie?: Movie) =>
          infoParams?.locReleaseDate !== false && !!(movie?.locReleaseDate || infoParams?.releaseDateEmptyMessage),
        showStreamingDate: (movie?: Movie) => !!movie?.streamingReleaseDate,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        showLocStreamingDate: () => false,
        enabledRedirections: () => ['genres']
      }
    };
    return this.getComponentProp('movieDetailInfo', prop) ?? DEFAULTS.movieMoreInfoParams[prop];
  }

  watchAtHomeComponent<K extends keyof WatchAtHomeParams>(prop: K): WatchAtHomeParams[K] | undefined {
    if (prop === 'dropdownIcon') {
      const dropdownIcon = this.getComponentProp('watchAtHome', prop);
      // Null is important because the default value is a custom one
      return dropdownIcon === null ? null : dropdownIcon || 'chevron-arrow-down';
    }
    return this.getComponentProp('watchAtHome', prop);
  }

  /**
   * Gets the full component configuration, considering route-based overrides.
   */
  private getComponentConfig<K extends keyof EnvironmentComponents>(
    componentKey: K
  ): EnvironmentComponents[K] extends EnvironmentComponentConfig<infer T> ? T | undefined : undefined {
    const componentConfig = this.env?.components?.[componentKey] as EnvironmentComponents[K] | undefined;

    if (!componentConfig) {
      return undefined;
    }

    const currentPath = this.router.url.split('?')[0];

    return (componentConfig.pageOverrides?.[currentPath] ?? componentConfig) as Extract<
      EnvironmentComponents[K] extends EnvironmentComponentConfig<infer T> ? T : undefined,
      NonNullable<unknown>
    >;
  }

  /**
   * Gets a deeply nested property from the component's configuration.
   */
  private getComponentProp<K extends keyof EnvironmentComponents, P extends string>(
    componentKey: K,
    prop: P
  ): DeepProperty<EnvironmentComponents[K] extends EnvironmentComponentConfig<infer U> ? U : never, P> | undefined {
    const config = this.getComponentConfig(componentKey);
    return config ? (getDeepProperty(config, prop) as any) : undefined;
  }
}

function getDeepProperty<T, P extends string>(obj: T, path: P): DeepProperty<T, P> | undefined {
  return path.split('.').reduce((acc, key) => acc && acc[key], obj) as DeepProperty<T, P> | undefined;
}

type DeepProperty<T, P extends string> = P extends `${infer K}.${infer Rest}`
  ? K extends keyof T
    ? DeepProperty<T[K], Rest>
    : never
  : P extends keyof T
    ? T[P]
    : never;

type MovieCatalogueKeys = NestedKeysOf<MovieCatalogueParams>;
/**
 * NestedKeysOf<T> produces a union of all valid "dot paths" of T.
 * Example:
 * type X = { filters?: { omitAddtionUserLocation?: boolean } };
 * NestedKeysOf<X> => "filters" | "filters.omitAddtionUserLocation"
 */
type NestedKeysOf<T> = T extends object
  ? {
      [K in keyof T & (string | number)]: NonNullable<T[K]> extends object
        ? // If T[K] is an object (ignoring undefined),
          // paths are either `K` or `K.subpath`.
          `${K}` | `${K}.${NestedKeysOf<NonNullable<T[K]>>}`
        : // Otherwise, just `K`.
          `${K}`;
    }[keyof T & (string | number)]
  : never;

/**
 * PathValue<T, P> returns the type of T at the dot path P.
 * For example,
 * PathValue<{ a: { b: number }}, "a.b"> = number
 */
type PathValue<T, P extends string> = P extends `${infer Head}.${infer Rest}`
  ? Head extends keyof T
    ? Rest extends string
      ? PathValue<T[Head], Rest>
      : never
    : never
  : P extends keyof T
    ? T[P]
    : never;
